// /invoicing/create — build ONE Alegra invoice that covers one or more
// unpaid reservations for the same client. Companion to the per-reservation
// Facturar button on /reservations/:id (which keeps using InvoiceModal).
//
// Flow:
//   A. Pick client (customer or company) via ClientSearch
//   B. Load their unpaid reservations via RPC get_unpaid_reservations_for_client
//   C. Check the reservations to include; each row has its own from/to date pickers
//   D. Review/edit the live-generated concepts (one subheader group per reservation)
//   E. Fill billing fields; stamp via shared invoicing utility
//
// When >1 reservation is selected, inserts `invoices_issued` with
// reservation_id = NULL + N `invoice_reservations` rows tagged per line.
// Single-reservation stamp keeps the same shape as InvoiceModal (reservation_id set).

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AppWrapper from "../AppWrapper";
import {
  Page,
  Card,
  BlockStack,
  Box,
  InlineGrid,
  InlineStack,
  Text,
  TextField,
  Select,
  Button,
  Banner,
  Divider,
  IndexTable,
  Checkbox,
  Badge,
  Spinner,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { formatPrice } from "../../utility/funcs";
import ClientSearch from "../CustomComponents/ClientSearch/ClientSearch";
import HoverArmButton from "../CustomComponents/HoverArmButton/HoverArmButton";
import {
  getReservationUnallocated,
  allocateAvailableToInvoice,
  alegraPaymentForm,
} from "../../utility/payments";
import {
  CFDI_USE_OPTIONS,
  REGIMEN_OPTIONS,
  PAYMENT_FORM_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
  CHARGE_TYPE_ALEGRA,
  CHARGE_TYPE_LABEL,
  alegraRequest,
  findOrCreateContact,
  saveBillingSettings,
  buildAlegraItems,
  applyRoundingAdjustment,
  buildInvoicePayload,
  buildRentLines,
  applyInvoiceDiscount,
  getReservationDiscountSummary,
  prefillBillingFromEntity,
  publicoGeneralBilling,
  reservationLastBillableDay,
  addDaysISO,
  getInvoicedConceptsForReservation,
  markChargesBilled,
  computeUninvoicedGaps,
  findPeriodOverlap,
  cfdiUuidOf,
} from "../../utility/invoicing";

// Parse a tstzrange into {from, to} date strings — both INCLUSIVE.
// `to` is the last billable day (first day + JS-rounded days − 1), not the
// raw upper(date_range)::date which is one day past the last billable day.
// buildRentLines treats periodTo as inclusive and adds +1 for exclusive math,
// so using the bare upper date would over-count days by exactly 1.
const parseRange = (range) => {
  if (!range) return { from: "", to: "" };
  const clean = String(range).replace(/[\[\]()]/g, "");
  const parts = clean.split(",");
  return {
    from: parts[0]?.replace(/"/g, "").trim().substring(0, 10) || "",
    to: reservationLastBillableDay(range),
  };
};

// Validate a picked invoice period against the reservation's own range.
// Returns an error string when invalid, or null when OK.
// Now that the default period can land on a gap anywhere in the span (not just
// after the last invoice), `billedPeriods` guards manual edits from re-billing
// a day that already has a CFDI — this page had no overlap check at all, unlike
// InvoiceModal.
const validatePeriod = (period, bounds, billedPeriods = []) => {
  if (!period?.from) return "Falta fecha inicial";
  if (!period?.to) return "Falta fecha final";
  if (period.from > period.to) return "Inicial posterior a final";
  if (bounds?.from && period.from < bounds.from)
    return "Inicio antes del alta";
  if (bounds?.to && period.to > bounds.to) return "Fin después del alta";
  const clash = findPeriodOverlap(period.from, period.to, billedPeriods);
  if (clash) {
    return `Traslape con factura ${clash.invoice_number || `#${clash.id}`} (${clash.period_from} al ${addDaysISO(clash.period_to || clash.period_from, 1)})`;
  }
  return null;
};

// Build default concept lines for a single reservation based on the RPC row + its
// from/to period. Multi-line rent (one per rate section) is fetched async from
// reservation_price_periods; the rest of the lines are computed here.
async function buildReservationItems(res, period, concepts) {
  const items = [];
  const { from, to } = period;
  // A fee (entrega / recolección / horas extra) is only proposed if its Alegra
  // product id hasn't already been billed on a prior invoice for this
  // reservation. Extra charges dedup by their own row id instead — several
  // charges can share a product — and rent is deduped by the `period` (which
  // the caller defaults to the un-invoiced remainder). When concepts haven't
  // loaded yet, nothing is treated as billed (matches the original
  // full-proposal behavior).
  const isBilled = concepts?.isConceptBilled || (() => false);
  const isChargeBilled = concepts?.isChargeBilled || (() => false);

  const totals = res.totals || {};
  const hoursExtra = totals.hoursExtra || 0;
  const deliveryFee = res.delivery?.fee || 0;
  const returnFee = res.return?.fee || 0;
  const chargesList = Array.isArray(res.charges) ? res.charges : [];

  const isSublease = !!res.is_sublease;
  const plate = isSublease ? res.sublease_plate || "" : res.car_plate || "";
  const alegraItemId = isSublease ? null : res.car_alegra_item_id || null;
  // For subleases the RPC COALESCEs the sublease brand/model into these fields;
  // they feed the line description since subleases carry no Alegra item.
  const subleaseBrand = isSublease ? res.car_brand_name || "" : "";
  const subleaseModel = isSublease ? res.car_model_name || "" : "";

  // Rent: one line per rate section overlapping [from, to].
  let discountApplied = 0;
  let discountDays = 0;
  if (from && to) {
    const rent = await buildRentLines(res.id, from, to, {
      plate, alegraItemId, isSublease, subleaseBrand, subleaseModel,
    });
    discountApplied = rent.discountApplied || 0;
    discountDays = rent.discountDays || 0;
    rent.lines.forEach((line, i) => {
      items.push({
        key: `res-${res.id}-rent-${i}`,
        ...line,
      });
    });
  }

  if (hoursExtra > 0 && !isBilled(271)) {
    items.push({
      key: `res-${res.id}-hours`,
      reservationId: res.id,
      description: "Horas extra",
      quantity: 1,
      unitPrice: hoursExtra,
      discount: 0,
      alegraItemId: 271,
    });
  }
  if (deliveryFee > 0 && !isBilled(272)) {
    items.push({
      key: `res-${res.id}-delivery`,
      reservationId: res.id,
      description: "Entrega a domicilio",
      quantity: 1,
      unitPrice: deliveryFee,
      discount: 0,
      alegraItemId: 272,
    });
  }
  if (returnFee > 0 && !isBilled(270)) {
    items.push({
      key: `res-${res.id}-return`,
      reservationId: res.id,
      description: "Recolección a domicilio",
      quantity: 1,
      unitPrice: returnFee,
      discount: 0,
      alegraItemId: 270,
    });
  }
  for (const charge of chargesList) {
    const chargeItemId = CHARGE_TYPE_ALEGRA[charge.charge_type] || 279;
    if (isChargeBilled(charge.id)) continue;
    const label =
      CHARGE_TYPE_LABEL[charge.charge_type] ||
      charge.description ||
      "Cargo extra";
    items.push({
      key: `res-${res.id}-charge-${charge.id}`,
      reservationId: res.id,
      chargeId: charge.id,
      description: label,
      quantity: 1,
      unitPrice: Number(charge.amount || 0),
      discount: 0,
      alegraItemId: chargeItemId,
      linePrefix:
        charge.description && charge.description !== label
          ? charge.description
          : null,
    });
  }
  return { items, discountApplied, discountDays };
}

function CreateInvoice() {
  const navigate = useNavigate();
  const { session } = UserAuth();

  // ---------- Client ----------
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  const [client, setClient] = useState(null); // { client_type, id, name, detail }
  const [entity, setEntity] = useState(null); // full row from customers/companies
  const [loadingClient, setLoadingClient] = useState(false);

  // ---------- Reservations ----------
  const [reservations, setReservations] = useState([]);
  const [loadingRes, setLoadingRes] = useState(false);
  const [selected, setSelected] = useState({}); // { [resId]: true }
  const [periods, setPeriods] = useState({});   // { [resId]: { from, to } }
  // Already-invoiced concepts per reservation (for partial-invoice dedup).
  // Keyed by reservation id; populated when reservations load. A ref (not state)
  // because buildReservationItems reads it synchronously and we don't want it to
  // retrigger the item-build effect on its own.
  const conceptsRef = useRef({}); // { [resId]: { periods, isConceptBilled, isChargeBilled, ... } }
  // Manual discount the user applies to each reservation ON THIS invoice, plus the
  // reservation's available (recorded − already-invoiced) discount for guidance.
  const [discountApply, setDiscountApply] = useState({}); // { [resId]: { days, amount } }
  const [discountSummary, setDiscountSummary] = useState({}); // { [resId]: summary }
  // What each reservation's applied discount resolves to (pesos + days), computed
  // alongside the effective line values and persisted onto invoices_issued.
  const discountResolvedRef = useRef({}); // { [resId]: { discountApplied, discountDays } }

  // ---------- Concepts (built from selected reservations) ----------
  const [items, setItems] = useState([]);

  // ---------- Billing / invoice-level ----------
  const [billing, setBilling] = useState({
    rfc: "",
    razonSocial: "",
    regimenFiscal: "",
    codigoPostal: "",
    cfdiUse: "G03",
    paymentForm: "transfer",
    paymentType: "PUE",
    creditDays: "",
  });
  const [invoiceNotes, setInvoiceNotes] = useState("");

  // Pay-first → PUE automation (across the selected reservations).
  const [availablePayment, setAvailablePayment] = useState(0);
  const [coverForm, setCoverForm] = useState(null);
  const [paymentTypeTouched, setPaymentTypeTouched] = useState(false);

  // ---------- Submit state ----------
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);

  // When a client is picked, load their billing profile + unpaid reservations.
  useEffect(() => {
    if (!client) {
      setEntity(null);
      setReservations([]);
      setSelected({});
      setPeriods({});
      setItems([]);
      return;
    }
    (async () => {
      setLoadingClient(true);
      // Fetch full entity for billing prefill
      const table = client.client_type === "company" ? "companies" : "customers";
      const { data: e } = await supabase
        .from(table)
        .select("*")
        .eq("id", client.id)
        .maybeSingle();
      setEntity(e || null);
      if (e) {
        const { billing: pf, emailTo: eto } = prefillBillingFromEntity(
          e,
          client.client_type === "company"
        );
        setBilling(pf);
        setEmailTo(eto || "");
      }
      setLoadingClient(false);

      // Fetch unpaid reservations
      setLoadingRes(true);
      const args =
        client.client_type === "company"
          ? { p_company_id: client.id, p_customer_id: null }
          : { p_customer_id: client.id, p_company_id: null };
      const { data: rows, error: rpcErr } = await supabase.rpc(
        "get_unpaid_reservations_for_client",
        args
      );
      if (rpcErr) {
        console.error(rpcErr);
        setReservations([]);
      } else {
        // The RPC returns unpaid reservations (balance > 0); narrow to those
        // with un-invoiced amount left so fully-invoiced-but-unpaid ones don't
        // show (they'd be double-invoiced). invoiced_sum is non-void invoice total.
        const uninvoiced = (rows || []).filter(
          (r) => (r.total_price || 0) - (r.invoiced_sum || 0) > 0.01
        );
        setReservations(uninvoiced);
        // Default each reservation's rent period to the UN-invoiced remainder,
        // and load its already-billed concepts (fees/charges) for dedup. We only
        // hit Alegra for reservations that actually have a prior invoice
        // (invoiced_sum > 0); fresh ones need no dedup.
        const defaults = {};
        const discSummaries = {};
        for (const r of uninvoiced) {
          const full = parseRange(r.date_range); // { from, to } (to = last billable day)
          // No prior invoice ⇒ nothing is billed yet, charges included (a
          // charge can only be linked to an invoice that exists).
          const NOTHING_BILLED = {
            periods: [],
            isConceptBilled: () => false,
            isChargeBilled: () => false,
          };
          let concepts = { ...NOTHING_BILLED, alegraReadOk: true };
          if ((r.invoiced_sum || 0) > 0.01) {
            try {
              concepts = await getInvoicedConceptsForReservation(r.id);
            } catch {
              concepts = { ...NOTHING_BILLED, alegraReadOk: false };
            }
          }
          conceptsRef.current[r.id] = concepts;
          // Available discount (recorded − already invoiced) for the control.
          try {
            discSummaries[r.id] = await getReservationDiscountSummary(r.id);
          } catch {
            discSummaries[r.id] = null;
          }

          // Default to the FIRST un-invoiced stretch of the reservation. This
          // walks the whole span instead of jumping past the latest invoice, so
          // a gap left BEFORE an already-billed period is still reachable (see
          // computeUninvoicedGaps). No gaps at all → blank period, which means a
          // fee/charge-only top-up: no rent line, and validation is skipped.
          const gaps = computeUninvoicedGaps(full.from, full.to, concepts.periods);
          defaults[r.id] = gaps[0] || { from: "", to: "" };
        }
        setPeriods(defaults);
        setDiscountSummary(discSummaries);
        // Default: apply the reservation's pending discount to this invoice so it
        // gets finished off. The user can clear it. Capped values, so applying the
        // whole remainder is safe (applyInvoiceDiscount trims to available rent).
        const discDefaults = {};
        for (const r of uninvoiced) {
          const s = discSummaries[r.id];
          const p = defaults[r.id] || {};
          // Cap the default day-drop at this invoice's own span so it never
          // zeroes the invoice by default (leftover days carry to the next one).
          const spanDays =
            p.from && p.to
              ? Math.round((new Date(p.to) - new Date(p.from)) / 86400000) + 1
              : 0;
          const defDays = s?.remainingDays > 0 ? Math.min(s.remainingDays, spanDays) : 0;
          discDefaults[r.id] = {
            days: defDays > 0 ? String(defDays) : "",
            amount: s?.remainingMoney > 0 ? String(s.remainingMoney) : "",
          };
        }
        setDiscountApply(discDefaults);
      }
      setLoadingRes(false);
    })();
  }, [client]);

  // Rebuild concept items whenever selection or periods change.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = [];
      for (const r of reservations) {
        if (!selected[r.id]) continue;
        const { items: lines } = await buildReservationItems(
          r,
          periods[r.id] || { from: "", to: "" },
          conceptsRef.current[r.id]
        );
        for (const l of lines) next.push({ ...l, enabled: true });
      }
      if (!cancelled) setItems(next);
    })();
    return () => { cancelled = true; };
  }, [selected, periods, reservations]);

  // ---------- Manual discount → effective per-line values ----------
  // Each reservation's rent lines are billed at full price; the user's per-invoice
  // discount (days dropped and/or a peso amount) is applied here via
  // applyInvoiceDiscount. effByKey maps item.key → { quantity, discount,
  // intendedTotal }; non-rent lines and undiscounted reservations use the raw item.
  const selectedReservationIds = Object.entries(selected)
    .filter(([, v]) => v)
    .map(([k]) => Number(k));
  const effByKey = useMemo(() => {
    const out = {};
    const resolved = {};
    for (const resId of selectedReservationIds) {
      const da = discountApply[resId] || {};
      const days = Math.max(0, parseInt(da.days, 10) || 0);
      const amount = Math.max(0, parseFloat(da.amount) || 0);
      if (days === 0 && amount === 0) { resolved[resId] = { discountApplied: 0, discountDays: 0 }; continue; }
      // ALL of this reservation's enabled lines are discountable, not just rent:
      // days only drop rent, but the peso amount spills over to fees/cargos when
      // rent can't absorb it (or when there's no rent left to bill at all).
      const discountable = items.filter(
        (it) => it.reservationId === resId && it.enabled
      );
      const res = applyInvoiceDiscount(discountable, { days, amount });
      resolved[resId] = { discountApplied: res.discountApplied, discountDays: res.discountDays };
      const seen = new Set();
      for (const l of res.lines) {
        out[l.key] = { quantity: l.quantity, discount: l.discount, intendedTotal: l.intendedTotal };
        seen.add(l.key);
      }
      // Any rent line fully dropped (all its days discounted) → quantity 0.
      for (const it of discountable) if (!seen.has(it.key)) out[it.key] = { quantity: 0, discount: 0, intendedTotal: 0 };
    }
    discountResolvedRef.current = resolved;
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, discountApply, JSON.stringify(selectedReservationIds)]);

  const effOf = (it) =>
    effByKey[it.key] || { quantity: it.quantity, discount: it.discount || 0, intendedTotal: it.intendedTotal };
  const lineGrossTotal = (it) => {
    const e = effOf(it);
    if (e.intendedTotal != null) return e.intendedTotal;
    const q = parseFloat(e.quantity) || 0;
    const up = parseFloat(it.unitPrice) || 0;
    const d = parseFloat(e.discount) || 0;
    return (up - d) * q;
  };

  // ---------- Totals (from enabled items, after discount) ----------
  const enabledItems = items.filter((i) => i.enabled);
  // `total` is the TRUE target the CFDI will be stamped at: each line's exact
  // intended gross (free of the 2-decimal per-unit discount rounding), summed
  // and rounded once.
  const total = Math.round(
    enabledItems.reduce((s, i) => s + lineGrossTotal(i), 0) * 100
  ) / 100;
  const subtotal = total / 1.16;
  const tax = total - subtotal;

  // Per-reservation validation against its own date_range bounds.
  const reservationBounds = {};
  for (const r of reservations) reservationBounds[r.id] = parseRange(r.date_range);
  const periodErrors = {};
  for (const rid of selectedReservationIds) {
    const p = periods[rid];
    // A blank period means all rent was already invoiced → this is a
    // fee/charge-only top-up; there's no rent range to validate.
    if (!p || (!p.from && !p.to)) continue;
    const err = validatePeriod(
      p,
      reservationBounds[rid],
      conceptsRef.current[rid]?.periods
    );
    if (err) periodErrors[rid] = err;
  }
  const hasInvalidPeriods = Object.keys(periodErrors).length > 0;

  // Can't apply more discount than the reservation has recorded (days or money).
  const hasDiscountError = selectedReservationIds.some((rid) => {
    const sum = discountSummary[rid];
    if (!sum) return false;
    const da = discountApply[rid] || {};
    const daysN = Math.max(0, parseInt(da.days, 10) || 0);
    const moneyN = Math.max(0, parseFloat(da.amount) || 0);
    return daysN > sum.remainingDays || moneyN > sum.remainingMoney + 0.005;
  });

  // Pay-first → PUE: total available unallocated payment across selected
  // reservations decides PUE (fully covered) vs PPD, until manually overridden.
  const selKey = selectedReservationIds.join(",");
  useEffect(() => {
    if (!selKey) {
      setAvailablePayment(0);
      setCoverForm(null);
      return;
    }
    getReservationUnallocated(selKey.split(",").map(Number)).then(
      ({ total: avail, transactions }) => {
        setAvailablePayment(avail);
        // Card-aware: a debit charge is forma 28, not 04.
        setCoverForm(alegraPaymentForm(transactions[0]) || null);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selKey]);

  useEffect(() => {
    if (paymentTypeTouched || !(total > 0)) return;
    const covered = availablePayment + 0.01 >= total;
    setBilling((prev) => ({
      ...prev,
      paymentType: covered ? "PUE" : "PPD",
      paymentForm: covered ? coverForm || prev.paymentForm : "other",
      creditDays: covered ? "" : prev.creditDays,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePayment, coverForm, total, paymentTypeTouched]);

  // ---------- Item editors ----------
  const patchItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const patchPeriod = (resId, patch) =>
    setPeriods((prev) => ({ ...prev, [resId]: { ...prev[resId], ...patch } }));

  // ---------- Submit ----------
  // `publicoGeneral` comes from the "Timbrar General" button: the CFDI goes to
  // the generic RFC (contact 1, S01, régimen 616, PUE) even though the client
  // has fiscal data on file. It's a per-invoice override — the form state and
  // the client record are left untouched.
  const submit = async ({ publicoGeneral = false } = {}) => {
    if (!client || selectedReservationIds.length === 0 || enabledItems.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const effBilling = publicoGeneral ? publicoGeneralBilling(billing) : billing;

      // 1. Alegra contact
      const contactId = await findOrCreateContact(effBilling);

      // 2. Persist billing prefs on the entity for next time. Skipped for a
      //    Público en General override: it would overwrite the client's real
      //    régimen / uso de CFDI and point alegra_contact_id at contact 1.
      if (!publicoGeneral) {
        await saveBillingSettings(effBilling, {
          customerId: client.client_type === "customer" ? client.id : null,
          companyId: client.client_type === "company" ? client.id : null,
          alegraContactId: contactId,
        });
      }

      // 3. Build Alegra line items — we don't pass a single periodFrom/To since
      //    each line carries its own period in its linePrefix. Pass nulls.
      const alegraLines = await buildAlegraItems(
        enabledItems
          .map((it) => {
            const e = effOf(it);
            const period = periods[it.reservationId] || {};
            // For rental lines, bake the period into the linePrefix so it shows up
            // on the CFDI even though we don't set an invoice-level period.
            const periodText =
              it.isRental && period.from && period.to
                ? `Periodo: ${period.from} al ${addDaysISO(period.to, 1)}`
                : "";
            const prefix = [it.linePrefix, periodText].filter(Boolean).join(". ");
            return {
              description: it.description,
              quantity: e.quantity,
              unitPrice: it.unitPrice,
              discount: e.discount || 0,
              alegraItemId: it.alegraItemId,
              linePrefix: prefix || null,
              isRental: !!it.isRental,
              isSublease: !!it.isSublease,
              reservationId: it.reservationId,
            };
          })
          // Rent lines fully consumed by a whole-day discount drop off the CFDI.
          .filter((l) => (parseFloat(l.quantity) || 0) > 0),
        { periodFrom: null, periodTo: null, invoiceNotes }
      );

      // 4. Rounding adjustment
      applyRoundingAdjustment(alegraLines, total);

      // 5. Stamp in Alegra
      const { payload: invoicePayload, today } = buildInvoicePayload({
        billing: effBilling,
        alegraItems: alegraLines,
        contactId,
        stamp: true,
      });
      const invoice = await alegraRequest("invoices", "POST", invoicePayload);

      // 6. Insert our DB header row. If only one reservation is selected,
      //    set reservation_id = that id (matches single-res invoice shape).
      //    Otherwise leave reservation_id null and write invoice_reservations rows.
      const singleRes = selectedReservationIds.length === 1 ? selectedReservationIds[0] : null;
      // Record the rent period for single-reservation invoices (same shape as
      // InvoiceModal) so later partial-invoice dedup can tell which rent days
      // are already billed. Fee/charge-only invoices carry a blank period.
      const singlePeriod = singleRes ? periods[singleRes] || {} : {};
      const { data: insertedRows, error: insertErr } = await supabase
        .from("invoices_issued")
        .insert({
          alegra_id: String(invoice.id),
          cfdi_uuid: cfdiUuidOf(invoice),
          reservation_id: singleRes,
          customer_id: client.client_type === "customer" ? client.id : null,
          company_id: client.client_type === "company" ? client.id : null,
          alegra_contact_id: String(contactId),
          invoice_number: invoice.numberTemplate?.fullNumber || String(invoice.id),
          date: today,
          subtotal,
          tax,
          total,
          status: invoice.status || "open",
          stamped: true,
          cfdi_use: effBilling.cfdiUse,
          payment_method: effBilling.paymentForm,
          payment_type: effBilling.paymentType,
          created_by: session?.user?.id,
          period_from: singlePeriod.from || null,
          period_to: singlePeriod.to || null,
          // Discount consumed by this invoice (only tracked for single-reservation
          // invoices, where the whole CFDI belongs to one reservation).
          discount_applied: singleRes
            ? discountResolvedRef.current[singleRes]?.discountApplied || 0
            : 0,
          discount_days: singleRes
            ? discountResolvedRef.current[singleRes]?.discountDays || 0
            : 0,
        })
        .select("id")
        .single();
      if (insertErr) throw insertErr;

      // 6b. Link the extra charges this CFDI actually billed, so the next
      //     invoice for these reservations doesn't propose them again. Only
      //     lines the user left enabled AND that survived the discount with a
      //     quantity count — an unticked charge stays billable.
      await markChargesBilled(
        enabledItems
          .filter((it) => it.chargeId && (parseFloat(effOf(it).quantity) || 0) > 0)
          .map((it) => it.chargeId),
        insertedRows.id
      );

      // 7. When multi-res, write invoice_reservations rows per Alegra line,
      //    tagged with the source reservation id so the read side can compute
      //    per-reservation portions.
      if (selectedReservationIds.length > 1) {
        const dbInvoiceId = insertedRows.id;
        const rows = enabledItems
          .map((it) => {
            const e = effOf(it);
            return {
              invoice_id: dbInvoiceId,
              reservation_id: it.reservationId,
              line_description: it.description,
              quantity: e.quantity,
              unit_price: it.unitPrice,
              discount: e.discount || 0,
              // Rent lines record this reservation's billed span (inclusive
              // period_to) so later gap/overlap checks see multi-res invoices.
              period_from: it.isRental ? it.sectionPeriodFrom || null : null,
              period_to: it.isRental ? it.sectionPeriodTo || null : null,
            };
          })
          .filter((r) => (parseFloat(r.quantity) || 0) > 0);
        if (rows.length > 0) {
          await supabase.from("invoice_reservations").insert(rows);
        }
      }

      // 7b. Pay-first → PUE: link the selected reservations' existing payment(s)
      //     to this invoice (PUE ⇒ no complemento; applyAllocations registers the
      //     payment in Alegra). Non-fatal — falls back to manual conciliation.
      if (effBilling.paymentType === "PUE") {
        try {
          await allocateAvailableToInvoice({
            invoice: {
              id: insertedRows.id,
              alegra_id: String(invoice.id),
              payment_type: "PUE",
              total,
              amount_paid: 0,
              customer_id: client.client_type === "customer" ? client.id : null,
              company_id: client.client_type === "company" ? client.id : null,
              alegra_contact_id: String(contactId),
              status: invoice.status,
            },
            reservationIds: selectedReservationIds,
            createdBy: session?.user?.id,
          });
        } catch (e) {
          console.error("PUE auto-allocation failed:", e);
        }
      }

      // 8. Fetch PDF/XML for the success banner
      let pdfUrl = null,
        xmlUrl = null;
      try {
        const withFiles = await alegraRequest(
          `invoices/${invoice.id}?fields=pdf,xml`,
          "GET"
        );
        pdfUrl = withFiles?.pdf || null;
        xmlUrl = withFiles?.xml || null;
      } catch {}

      setSuccess({
        id: invoice.id,
        number: invoice.numberTemplate?.fullNumber || invoice.id,
        pdfUrl,
        xmlUrl,
      });
    } catch (err) {
      const draftId = err.alegraResponse?.invoice?.id;
      if (draftId) {
        try {
          await alegraRequest(`invoices/${draftId}`, "DELETE");
        } catch {}
      }
      setError(err.message || "Error al crear factura");
    } finally {
      setSaving(false);
    }
  };

  const sendEmail = async () => {
    if (!emailTo.trim() || !success) return;
    setEmailSending(true);
    setEmailError(null);
    try {
      await alegraRequest(`invoices/${success.id}/email`, "POST", {
        emails: [emailTo.trim()],
        sendCopyToUser: false,
      });
      setEmailSent(true);
    } catch (e) {
      setEmailError(e.message || "Error al enviar el correo");
    } finally {
      setEmailSending(false);
    }
  };

  // ---------- Render ----------

  // Shared by Timbrar and Timbrar General — both stamp the same invoice.
  const stampDisabled =
    saving ||
    !client ||
    selectedReservationIds.length === 0 ||
    enabledItems.length === 0 ||
    total <= 0 ||
    hasInvalidPeriods ||
    hasDiscountError;

  const reservationHeadings = [
    { title: "Incluir" },
    { title: "Reserva" },
    { title: "Auto" },
    { title: "Desde" },
    { title: "Hasta" },
    { title: "Saldo" },
  ];

  return (
    <AppWrapper>
      <Page
        title="Nueva Factura"
        backAction={{ url: "/dashboard" }}
        primaryAction={
          !success
            ? {
                content: saving ? "Timbrando..." : "Timbrar",
                onAction: () => submit(),
                loading: saving,
                disabled: stampDisabled,
              }
            : {
                content: "Nueva factura",
                onAction: () => window.location.reload(),
              }
        }
        // Sits to the left of Timbrar. Same invoice, stamped to the generic RFC
        // instead of the client's — armed only after a 4s hover so it can't be
        // hit by accident.
        secondaryActions={
          !success ? (
            <HoverArmButton
              onAction={() => submit({ publicoGeneral: true })}
              loading={saving}
              disabled={stampDisabled}
            >
              Timbrar General
            </HoverArmButton>
          ) : undefined
        }
      >
        <BlockStack gap="400">
          {error && (
            <Banner tone="critical">
              <p>{error}</p>
            </Banner>
          )}
          {hasInvalidPeriods && (
            <Banner tone="warning">
              <p>
                Hay reservaciones con periodos inválidos. Corrige las fechas
                antes de timbrar.
              </p>
            </Banner>
          )}
          {success && (
            <Card>
              <BlockStack gap="400">
                <InlineStack gap="200" blockAlign="center">
                  <Badge tone="success" size="large">Timbrada</Badge>
                  <Text variant="headingLg">Factura {success.number}</Text>
                </InlineStack>
                <Text tone="subdued">
                  {client?.name}
                  {selectedReservationIds.length > 0
                    ? ` · ${selectedReservationIds.length} reservación(es) · ${formatPrice(total)}`
                    : ""}
                </Text>
                <InlineStack gap="200">
                  {success.pdfUrl && (
                    <Button url={success.pdfUrl} external>Descargar PDF</Button>
                  )}
                  {success.xmlUrl && (
                    <Button url={success.xmlUrl} external>Descargar XML</Button>
                  )}
                </InlineStack>

                <Divider />
                <Text variant="headingSm">Enviar por correo</Text>
                {emailSent ? (
                  <Banner tone="success">Correo enviado a {emailTo}.</Banner>
                ) : (
                  <>
                    <InlineStack gap="200" blockAlign="end">
                      <div style={{ minWidth: 300 }}>
                        <TextField
                          label="Correo"
                          labelHidden
                          type="email"
                          value={emailTo}
                          onChange={setEmailTo}
                          autoComplete="off"
                          placeholder="cliente@correo.com"
                        />
                      </div>
                      <Button onClick={sendEmail} loading={emailSending} disabled={!emailTo.trim()}>
                        Enviar
                      </Button>
                    </InlineStack>
                    {emailError && (
                      <Banner tone="critical" onDismiss={() => setEmailError(null)}>
                        {emailError}
                      </Banner>
                    )}
                  </>
                )}

                <Divider />
                <div>
                  <Button variant="primary" onClick={() => window.location.reload()}>
                    Crear otra factura
                  </Button>
                </div>
              </BlockStack>
            </Card>
          )}

          {!success && (
          <>
          {/* --- Client --- */}
          <Card>
            <BlockStack gap="300">
              <Text variant="headingSm">Cliente</Text>
              {client ? (
                <InlineGrid columns="1fr auto" alignItems="center">
                  <BlockStack gap="050">
                    <Text variant="bodyMd" fontWeight="semibold">
                      {client.name}
                    </Text>
                    <Text variant="bodySm" tone="subdued">
                      {client.detail}
                    </Text>
                  </BlockStack>
                  <Button
                    variant="plain"
                    onClick={() => setClient(null)}
                    disabled={saving || !!success}
                  >
                    Cambiar
                  </Button>
                </InlineGrid>
              ) : (
                <Button onClick={() => setClientSearchOpen(true)}>
                  Seleccionar cliente
                </Button>
              )}
            </BlockStack>
          </Card>

          {/* --- Reservations --- */}
          {client && (
            <Card>
              <BlockStack gap="300">
                <Text variant="headingSm">Reservaciones</Text>
                {loadingRes ? (
                  <Spinner size="small" />
                ) : reservations.length === 0 ? (
                  <Text tone="subdued">
                    Este cliente no tiene reservaciones por facturar.
                  </Text>
                ) : (
                  <IndexTable
                    resourceName={{ singular: "reservación", plural: "reservaciones" }}
                    selectable={false}
                    itemCount={reservations.length}
                    headings={reservationHeadings}
                  >
                    {reservations.map((r, i) => {
                      const period = periods[r.id] || { from: "", to: "" };
                      const bounds = reservationBounds[r.id] || {};
                      const err = selected[r.id]
                        ? validatePeriod(
                            period,
                            bounds,
                            conceptsRef.current[r.id]?.periods
                          )
                        : null;
                      return (
                        <IndexTable.Row
                          id={String(r.id)}
                          key={r.id}
                          position={i}
                        >
                          <IndexTable.Cell>
                            <Checkbox
                              label=""
                              labelHidden
                              checked={!!selected[r.id]}
                              onChange={(v) =>
                                setSelected((prev) => ({ ...prev, [r.id]: v }))
                              }
                            />
                          </IndexTable.Cell>
                          <IndexTable.Cell>
                            <Text variant="bodySm" fontWeight="semibold">
                              #{r.id}
                            </Text>
                          </IndexTable.Cell>
                          <IndexTable.Cell>
                            <Text variant="bodySm">
                              {[
                                r.car_brand_name,
                                r.car_model_name,
                                (r.car_plate || r.sublease_plate)
                                  ? `(${r.car_plate || r.sublease_plate})`
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(" ") || "—"}
                              {r.is_sublease && (
                                <>
                                  {" "}
                                  <Badge tone="info">Subarrendado</Badge>
                                </>
                              )}
                            </Text>
                          </IndexTable.Cell>
                          <IndexTable.Cell>
                            <TextField
                              label=""
                              labelHidden
                              type="date"
                              value={period.from}
                              onChange={(v) => patchPeriod(r.id, { from: v })}
                              autoComplete="off"
                              min={bounds.from || undefined}
                              max={bounds.to || undefined}
                              error={err || undefined}
                            />
                          </IndexTable.Cell>
                          <IndexTable.Cell>
                            <TextField
                              label=""
                              labelHidden
                              type="date"
                              // Shown as the CHECKOUT day (the return date), which
                              // is NOT billed — so "Jun 1 → Jun 7" reads as 6 days
                              // (rental-night convention) and the next invoice
                              // starts Jun 7. Internally period.to stays the last
                              // BILLED day (inclusive) to match buildRentLines and
                              // stored periods, hence +1 on display / −1 on change.
                              // Same convention as InvoiceModal's "Hasta" field.
                              value={period.to ? addDaysISO(period.to, 1) : ""}
                              onChange={(v) =>
                                patchPeriod(r.id, {
                                  to: v ? addDaysISO(v, -1) : "",
                                })
                              }
                              autoComplete="off"
                              min={
                                period.from
                                  ? addDaysISO(period.from, 1)
                                  : bounds.from
                                  ? addDaysISO(bounds.from, 1)
                                  : undefined
                              }
                              max={bounds.to ? addDaysISO(bounds.to, 1) : undefined}
                            />
                          </IndexTable.Cell>
                          <IndexTable.Cell>
                            <Text variant="bodySm">
                              {formatPrice(r.balance)}
                            </Text>
                          </IndexTable.Cell>
                        </IndexTable.Row>
                      );
                    })}
                  </IndexTable>
                )}
              </BlockStack>
            </Card>
          )}

          {/* --- Concepts --- */}
          {selectedReservationIds.length > 0 && (
            <Card>
              <BlockStack gap="300">
                <Text variant="headingSm">Conceptos</Text>
                {items.length === 0 ? (
                  <Text tone="subdued">
                    Las reservaciones seleccionadas no tienen conceptos pendientes.
                  </Text>
                ) : (
                  <BlockStack gap="200">
                    {(() => {
                      // Group items by reservation id, rendered with a subheader.
                      const groups = [];
                      for (const resId of selectedReservationIds) {
                        const resRows = items
                          .map((it, idx) => ({ ...it, idx }))
                          .filter((it) => it.reservationId === resId);
                        if (resRows.length === 0) continue;
                        groups.push({ resId, rows: resRows });
                      }
                      return groups.map(({ resId, rows }) => {
                        const r = reservations.find((x) => x.id === resId);
                        return (
                          <div
                            key={resId}
                            style={{
                              border: "1px solid var(--p-color-border)",
                              borderRadius: 8,
                              padding: 12,
                            }}
                          >
                            <BlockStack gap="200">
                              <Text variant="bodyMd" fontWeight="semibold">
                                {[
                                  `Reserva #${resId}`,
                                  [
                                    r?.car_brand_name,
                                    r?.car_model_name,
                                    (r?.car_plate || r?.sublease_plate)
                                      ? `(${r?.car_plate || r?.sublease_plate})`
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .join(" "),
                                ]
                                  .filter(Boolean)
                                  .join(" — ")}
                              </Text>
                              {(() => {
                                const sum = discountSummary[resId];
                                // Only reservations with a RECORDED, still-available
                                // discount get a control — no ad-hoc discounts here.
                                if (!sum || (sum.remainingDays <= 0 && sum.remainingMoney <= 0)) {
                                  return null;
                                }
                                const da = discountApply[resId] || {};
                                const rate =
                                  items.find((it) => it.reservationId === resId && it.isRental)
                                    ?.unitPrice || sum.dailyPrice || 0;
                                const daysN = Math.max(0, parseInt(da.days, 10) || 0);
                                const moneyN = Math.max(0, parseFloat(da.amount) || 0);
                                // What the lines could actually absorb (the memo
                                // above already resolved it), not what was typed —
                                // they differ when there isn't enough to discount.
                                const res = discountResolvedRef.current[resId] || {};
                                const applied =
                                  (res.discountDays || 0) * rate + (res.discountApplied || 0);
                                const overDays = daysN > sum.remainingDays;
                                const overMoney = moneyN > sum.remainingMoney + 0.005;
                                const nInputs =
                                  (sum.remainingDays > 0 ? 1 : 0) +
                                  (sum.remainingMoney > 0 ? 1 : 0);
                                return (
                                  <Box
                                    background="bg-surface-secondary"
                                    padding="200"
                                    borderRadius="200"
                                  >
                                    <BlockStack gap="150">
                                      <Text variant="bodySm" tone="subdued">
                                        Descuento de reserva disponible:{" "}
                                        {sum.remainingDays > 0
                                          ? `${sum.remainingDays} día${sum.remainingDays === 1 ? "" : "s"}`
                                          : ""}
                                        {sum.remainingDays > 0 && sum.remainingMoney > 0 ? " · " : ""}
                                        {sum.remainingMoney > 0 ? formatPrice(sum.remainingMoney) : ""}
                                      </Text>
                                      <InlineGrid columns={nInputs === 2 ? "1fr 1fr" : "1fr"} gap="200">
                                        {sum.remainingDays > 0 && (
                                          <TextField
                                            label="Días a descontar"
                                            type="number"
                                            min={0}
                                            max={sum.remainingDays}
                                            value={String(da.days ?? "")}
                                            onChange={(v) =>
                                              setDiscountApply((prev) => ({
                                                ...prev,
                                                [resId]: { ...prev[resId], days: v },
                                              }))
                                            }
                                            suffix={rate ? `× ${formatPrice(rate)}` : undefined}
                                            error={overDays ? `Máx ${sum.remainingDays}` : undefined}
                                            autoComplete="off"
                                          />
                                        )}
                                        {sum.remainingMoney > 0 && (
                                          <TextField
                                            label="Monto a descontar"
                                            type="number"
                                            min={0}
                                            prefix="$"
                                            value={String(da.amount ?? "")}
                                            onChange={(v) =>
                                              setDiscountApply((prev) => ({
                                                ...prev,
                                                [resId]: { ...prev[resId], amount: v },
                                              }))
                                            }
                                            error={overMoney ? `Máx ${formatPrice(sum.remainingMoney)}` : undefined}
                                            autoComplete="off"
                                          />
                                        )}
                                      </InlineGrid>
                                      {applied > 0 && (
                                        <Text variant="bodySm" tone="success">
                                          Descuento aplicado en esta factura: {formatPrice(applied)}
                                        </Text>
                                      )}
                                    </BlockStack>
                                  </Box>
                                );
                              })()}
                              <InlineGrid
                                columns="auto 3fr 1fr 1fr 1fr 1fr"
                                gap="200"
                                alignItems="center"
                              >
                                <Text variant="bodySm" tone="subdued">
                                  Incl.
                                </Text>
                                <Text variant="bodySm" tone="subdued">
                                  Descripción
                                </Text>
                                <Text variant="bodySm" tone="subdued">
                                  Cantidad
                                </Text>
                                <Text variant="bodySm" tone="subdued">
                                  Precio
                                </Text>
                                <Text variant="bodySm" tone="subdued">
                                  Descuento
                                </Text>
                                <Text variant="bodySm" tone="subdued">
                                  Total
                                </Text>
                              </InlineGrid>
                              {rows.map((it) => {
                                const e = effOf(it);
                                const qty = parseFloat(e.quantity) || 0;
                                const unit = parseFloat(it.unitPrice) || 0;
                                const disc = parseFloat(e.discount) || 0;
                                const lineTotal = qty * unit - qty * disc;
                                return (
                                  <InlineGrid
                                    key={it.key}
                                    columns="auto 3fr 1fr 1fr 1fr 1fr"
                                    gap="200"
                                    alignItems="center"
                                  >
                                    <Checkbox
                                      label=""
                                      labelHidden
                                      checked={!!it.enabled}
                                      onChange={(v) =>
                                        patchItem(it.idx, { enabled: v })
                                      }
                                    />
                                    <Text variant="bodySm">{it.description}</Text>
                                    <Text variant="bodySm">{qty}</Text>
                                    <Text variant="bodySm">
                                      {formatPrice(unit)}
                                    </Text>
                                    <Text variant="bodySm">
                                      {disc > 0 ? formatPrice(disc) : "—"}
                                    </Text>
                                    <Text variant="bodySm">
                                      {formatPrice(lineTotal)}
                                    </Text>
                                  </InlineGrid>
                                );
                              })}
                            </BlockStack>
                          </div>
                        );
                      });
                    })()}
                    <Divider />
                    <InlineGrid columns="1fr auto">
                      <Text variant="bodyMd">Subtotal</Text>
                      <Text variant="bodyMd">{formatPrice(subtotal)}</Text>
                    </InlineGrid>
                    <InlineGrid columns="1fr auto">
                      <Text variant="bodyMd">IVA (16%)</Text>
                      <Text variant="bodyMd">{formatPrice(tax)}</Text>
                    </InlineGrid>
                    <InlineGrid columns="1fr auto">
                      <Text variant="headingSm" fontWeight="bold">
                        Total
                      </Text>
                      <Text variant="headingSm" fontWeight="bold">
                        {formatPrice(total)}
                      </Text>
                    </InlineGrid>
                  </BlockStack>
                )}
              </BlockStack>
            </Card>
          )}

          {/* --- Billing --- */}
          {selectedReservationIds.length > 0 && (
            <Card>
              <BlockStack gap="300">
                <Text variant="headingSm">Datos de Facturación</Text>
                <InlineGrid columns={2} gap="200">
                  <TextField
                    label="RFC"
                    value={billing.rfc}
                    onChange={(v) =>
                      setBilling({ ...billing, rfc: v.toUpperCase() })
                    }
                    disabled
                  />
                  <TextField
                    label="Razón Social"
                    value={billing.razonSocial}
                    onChange={(v) =>
                      setBilling({ ...billing, razonSocial: v })
                    }
                    disabled
                  />
                </InlineGrid>
                <InlineGrid columns={3} gap="200">
                  <Select
                    label="Régimen Fiscal"
                    value={billing.regimenFiscal}
                    options={REGIMEN_OPTIONS}
                    onChange={(v) =>
                      setBilling({ ...billing, regimenFiscal: v })
                    }
                    disabled
                  />
                  <TextField
                    label="Código Postal"
                    value={billing.codigoPostal}
                    onChange={(v) =>
                      setBilling({
                        ...billing,
                        codigoPostal: v.replace(/\D/g, "").substring(0, 5),
                      })
                    }
                    maxLength={5}
                    disabled
                  />
                  <Select
                    label="Uso de CFDI"
                    value={billing.cfdiUse}
                    options={CFDI_USE_OPTIONS}
                    onChange={(v) => setBilling({ ...billing, cfdiUse: v })}
                    disabled={billing.rfc === "XAXX010101000"}
                  />
                </InlineGrid>
                <InlineGrid columns={billing.paymentType === "PPD" ? 3 : 2} gap="200">
                  <Select
                    label="Método de Pago"
                    value={billing.paymentType}
                    options={PAYMENT_TYPE_OPTIONS}
                    helpText={`Pago disponible: ${formatPrice(availablePayment)} · auto: ${availablePayment + 0.01 >= total && total > 0 ? "PUE" : "PPD"}`}
                    onChange={(v) => {
                      setPaymentTypeTouched(true);
                      setBilling({
                        ...billing,
                        paymentType: v,
                        // SAT rule: PPD invoices must use Forma de Pago
                        // "99 — Por definir". Lock it when switching to PPD.
                        paymentForm:
                          v === "PPD" ? "other" : billing.paymentForm,
                      });
                    }}
                  />
                  {billing.paymentType === "PPD" && (
                    <TextField
                      label="Días de crédito"
                      type="number"
                      min={0}
                      suffix="días"
                      value={
                        billing.creditDays === "" || billing.creditDays == null
                          ? ""
                          : String(billing.creditDays)
                      }
                      onChange={(v) =>
                        setBilling({ ...billing, creditDays: v.replace(/\D/g, "") })
                      }
                      autoComplete="off"
                      helpText={`Vence: ${addDaysISO(
                        new Date().toLocaleDateString("en-CA"),
                        parseInt(billing.creditDays, 10) || 0
                      )}`}
                    />
                  )}
                  <Select
                    label="Forma de Pago"
                    value={billing.paymentForm}
                    options={
                      billing.paymentType === "PPD"
                        ? PAYMENT_FORM_OPTIONS.filter(
                            (o) => o.value === "other"
                          )
                        : PAYMENT_FORM_OPTIONS
                    }
                    onChange={(v) =>
                      setBilling({ ...billing, paymentForm: v })
                    }
                    disabled={billing.paymentType === "PPD"}
                    helpText={
                      billing.paymentType === "PPD"
                        ? "PPD sólo admite 99 — Por definir."
                        : undefined
                    }
                  />
                </InlineGrid>
                <TextField
                  label="Notas (opcional)"
                  value={invoiceNotes}
                  onChange={setInvoiceNotes}
                  multiline={2}
                  placeholder="Texto adicional que se incluye en cada línea de la factura"
                />
              </BlockStack>
            </Card>
          )}
          </>
          )}
        </BlockStack>

        <ClientSearch
          open={clientSearchOpen}
          onClose={() => setClientSearchOpen(false)}
          onSelect={(r) => {
            setClient(r);
            setClientSearchOpen(false);
          }}
        />
      </Page>
    </AppWrapper>
  );
}

export default CreateInvoice;
